import { useEffect } from "react";
import { getSiteUrl } from "@/lib/site";

// Injects a JSON-LD <script> into <head>. `data` may be an object or an array.
// Structured data is generated from the same sources as the rendered content.
export const JsonLd = ({ id, data }) => {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [id, data]);
  return null;
};

// Structured-data URLs use the centralized production site URL so schema always
// references the live domain (never localhost or the preview host).
const ORIGIN = getSiteUrl();

// Stable, reusable entity @id values referenced across the whole site.
export const PERSON_ID = `${ORIGIN}/#shawn-king`;
export const ORG_ID = `${ORIGIN}/#organization`;
export const SITE_ID = `${ORIGIN}/#website`;
export const NNI_VIDEO_ID = `${ORIGIN}/about#nni-interview-video`;

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Legacy Life & Retirement",
  url: `${ORIGIN}/`,
  logo: `${ORIGIN}/images/legacy-life-retirement-logo.png`,
  description:
    "A financial-education platform focused on Infinite Banking, cash flow, retirement planning, and family legacy.",
  founder: { "@id": PERSON_ID },
  sameAs: [
    "https://www.youtube.com/@LegacyLifeandRetirement",
    "https://www.instagram.com/legacylifeandretirement/",
    "https://podcasts.apple.com/us/podcast/the-cashflow-chronicles/id1768314180",
    "https://open.spotify.com/show/1gtZe3qKBYMRgkyTkJ4D9o",
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": SITE_ID,
  name: "Legacy Life & Retirement",
  url: `${ORIGIN}/`,
  publisher: { "@id": ORG_ID },
  inLanguage: "en-US",
});

export const personSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": PERSON_ID,
  name: "Shawn King",
  url: `${ORIGIN}/about`,
  image: `${ORIGIN}/images/shawn-king.png`,
  jobTitle: "Authorized Infinite Banking Practitioner",
  worksFor: { "@id": ORG_ID },
  description:
    "Shawn King is an Authorized Infinite Banking Practitioner, financial educator, author of From Regular to Rich, host of The Cashflow Chronicles, and founder of Legacy Life & Retirement.",
  knowsAbout: [
    "Infinite Banking", "Cash flow", "Whole life insurance", "Retirement planning",
    "Long-term care planning", "Annuities", "Family legacy planning",
  ],
  sameAs: [
    "https://www.youtube.com/@LegacyLifeandRetirement",
    "https://infinitebanking.org/practitioners/king882/",
    "https://www.linkedin.com/in/shawnkingsfg/",
  ],
  subjectOf: { "@id": NNI_VIDEO_ID },
});

// Generic WebPage entity linking a page to the WebSite + publisher (author optional).
export const webPageSchema = ({ path, name, withAuthor = false }) => {
  const url = `${ORIGIN}${path === "/" ? "/" : path}`;
  const obj = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    isPartOf: { "@id": SITE_ID },
    publisher: { "@id": ORG_ID },
  };
  if (name) obj.name = name;
  if (withAuthor) obj.author = { "@id": PERSON_ID };
  return obj;
};

export const profilePageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfilePage",
  "@id": `${ORIGIN}/about#webpage`,
  url: `${ORIGIN}/about`,
  isPartOf: { "@id": SITE_ID },
  mainEntity: { "@id": PERSON_ID },
  about: { "@id": PERSON_ID },
  hasPart: { "@id": NNI_VIDEO_ID },
});

export const nniInterviewVideoSchema = () => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "@id": NNI_VIDEO_ID,
  name: "How to Start IBC at Any Age and Take Control of Your Finances",
  description:
    "Shawn King shares his journey discovering the Infinite Banking Concept, why he moved from IUL to properly structured whole life insurance, and how managing cash flow, using policy loans, and thinking long-term can create greater financial flexibility and control.",
  thumbnailUrl: ["https://i.ytimg.com/vi/iVnoFZpVcqg/maxresdefault.jpg"],
  uploadDate: "2026-02-02T01:00:58-08:00",
  duration: "PT45M53S",
  embedUrl: "https://www.youtube.com/embed/iVnoFZpVcqg",
  url: "https://www.youtube.com/watch?v=iVnoFZpVcqg",
  inLanguage: "en-US",
  about: { "@id": PERSON_ID },
  publisher: {
    "@type": "Organization",
    name: "Nelson Nash Institute",
    url: "https://infinitebanking.org/",
  },
});

export const bookSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Book",
  name: "From Regular to Rich",
  author: { "@type": "Person", name: "Shawn King" },
  url: "https://fromregulartorich.com",
  inLanguage: "en",
});

export const videoObjectSchema = ({ name, description, videoId }) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name,
  description,
  thumbnailUrl: [`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`],
  embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}`,
});

// items: [{ name, path }]
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: ORIGIN ? `${ORIGIN}${it.path}` : undefined,
  })),
});

// faqs: [{ q, a }] — must match the visible FAQ content exactly.
export const faqPageSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${ORIGIN}/faqs#faqpage`,
  isPartOf: { "@id": SITE_ID },
  author: { "@id": PERSON_ID },
  publisher: { "@id": ORG_ID },
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
